import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, graphql } from 'react-relay';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DaysheetlyButton } from '@/components/ui/DaysheetlyButton';
import {
  WorkspaceFlowBrand,
  WorkspaceFlowShell,
} from '@/features/workspace/components/WorkspaceFlowShell';
import { CheckCircle2, Mail, Sparkles, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import type { AcceptInvitePageMutation as AcceptInvitePageMutationType } from '@/__generated__/AcceptInvitePageMutation.graphql';

const acceptMutation = graphql`
  mutation AcceptInvitePageMutation($token: String!) {
    acceptWorkspaceInvite(token: $token) {
      workspace { id name }
      member { role }
      invite { email }
      requiresAccount
      requiresLogin
    }
  }
`;

type AccountPrompt = {
  workspaceName: string;
  inviteEmail: string;
};

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, setActiveWorkspaceId } = useAuth();
  const token = searchParams.get('token');
  const resumeAcceptedInvite = searchParams.get('resume') === 'accepted';
  const [state, setState] = useState<'idle' | 'accepting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [accountPrompt, setAccountPrompt] = useState<AccountPrompt | null>(null);
  const [commit] = useMutation<AcceptInvitePageMutationType>(acceptMutation);

  const accept = useCallback(() => {
    if (!token) return;
    setState('accepting');
    commit({
      variables: { token },
      onCompleted: (response, errors) => {
        if (errors?.[0]) {
          setState('error');
          setErrorMsg(errors[0].message);
          return;
        }
        const result = response.acceptWorkspaceInvite;
        if (result.requiresLogin) {
          const next = `/accept-invite?token=${encodeURIComponent(token)}`;
          navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
          return;
        }
        if (result.requiresAccount) {
          setState('idle');
          setAccountPrompt({
            workspaceName: result.workspace.name,
            inviteEmail: result.invite.email,
          });
          return;
        }

        const workspaceId = result.workspace.id;
        setActiveWorkspaceId(workspaceId);
        toast.success(`Welcome to ${result.workspace.name}`);
        navigate('/dashboard', { replace: true });
      },
      onError: (err) => {
        setState('error');
        setErrorMsg(err.message);
      },
    });
  }, [commit, navigate, setActiveWorkspaceId, token]);

  useEffect(() => {
    if (isAuthenticated && resumeAcceptedInvite) {
      accept();
    }
  }, [accept, isAuthenticated, resumeAcceptedInvite]);

  if (!token) {
    return null;
  }

  return (
    <>
      <WorkspaceFlowShell>
        <div
          data-testid="accept-invite-card"
          className="w-full rounded-[28px] border bg-background/95 px-6 py-8 shadow-2xl shadow-blue-900/20 backdrop-blur sm:px-10 sm:py-12 lg:px-14 lg:py-14"
        >
          <WorkspaceFlowBrand />
          <div className="text-center">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Workspace invite
            </h1>
            <p className="mx-auto mt-3 max-w-md text-base leading-7 text-muted-foreground sm:text-lg">
              You&apos;ve been invited to join a workspace.
            </p>
          </div>

          {state === 'error' ? (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
              data-testid="accept-invite-error"
            >
              {errorMsg ?? 'This invite cannot be accepted.'}
              <div className="mt-4">
                <DaysheetlyButton
                  variant="outline"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login', { replace: true })}
                >
                  {isAuthenticated ? 'Go to dashboard' : 'Go to login'}
                </DaysheetlyButton>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-10 rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-sm sm:p-8">
                <div className="relative mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-blue-100/80 text-blue-700">
                  <Mail className="h-12 w-12" strokeWidth={1.6} aria-hidden="true" />
                  <Users className="absolute bottom-4 right-3 h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
                  <Sparkles className="absolute -right-5 top-1 h-5 w-5 text-amber-400" aria-hidden="true" />
                  <Sparkles className="absolute -left-5 top-10 h-4 w-4 text-blue-400" aria-hidden="true" />
                </div>
                <p className="text-base text-muted-foreground">You&apos;ve been invited to join</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                  your team workspace
                </h2>
                <div className="mt-6 flex items-center gap-3 border-t pt-5 text-left">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700">
                    {user?.name
                      ?.split(' ')
                      .map((part) => part[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() ?? 'DS'}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">
                      Invitation for {user?.email ?? 'your invited email'}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Continue with the email that received this invite.
                    </p>
                  </div>
                </div>
              </div>

              {user ? (
                <div className="mt-5 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>Signed in as {user.name}.</span>
                </div>
              ) : null}

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <DaysheetlyButton
                  onClick={accept}
                  loading={state === 'accepting'}
                  data-testid="accept-invite-button"
                  size="lg"
                  fullWidth
                >
                  {state === 'accepting' ? 'Joining…' : 'Accept invite'}
                </DaysheetlyButton>
                <DaysheetlyButton
                  variant="outline"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login', { replace: true })}
                  size="lg"
                  fullWidth
                >
                  Not now
                </DaysheetlyButton>
              </div>
            </>
          )}
        </div>
      </WorkspaceFlowShell>

      <Dialog open={Boolean(accountPrompt)} onOpenChange={(open) => !open && setAccountPrompt(null)}>
        <DialogContent data-testid="accept-invite-account-dialog">
          <DialogHeader>
            <DialogTitle>You joined {accountPrompt?.workspaceName}</DialogTitle>
            <DialogDescription>
              Your place in this workspace is reserved. You do not have a Day
              Sheetly account yet, so create one to access it.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">Account not created yet</p>
            <p className="mt-1 text-muted-foreground">
              Create your account with {accountPrompt?.inviteEmail} to finish
              joining this workspace.
            </p>
          </div>
          <DialogFooter>
            <DaysheetlyButton
              data-testid="accept-invite-create-account"
              onClick={() => {
                if (!accountPrompt) return;
                navigate(
                  `/register?inviteToken=${encodeURIComponent(token)}&email=${encodeURIComponent(accountPrompt.inviteEmail)}&inviteAccepted=1`,
                  { replace: true },
                );
              }}
            >
              Create account
            </DaysheetlyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
